import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import useGameStore from '../engine/store';
import { ACHIEVEMENTS } from '../engine/achievements';

function formatMoney(value) {
  if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

export default function GameOverScreen() {
  const {
    month, totalCapital, initialCapital, totalProfit, totalLoansApproved,
    totalLoansRejected, defaultsLog, completedLoansLog, difficulty,
    monthlyHistory, unlockedAchievements, resetGame, reputation,
  } = useGameStore();

  const roi = initialCapital > 0 ? ((totalCapital - initialCapital) / initialCapital * 100) : 0;
  const defaultRate = totalLoansApproved > 0 ? ((defaultsLog.length / totalLoansApproved) * 100) : 0;

  const chartData = monthlyHistory.map(m => ({
    month: m.month,
    capital: m.capital,
  }));

  // Score
  const score = Math.max(0,
    month * 10
    + (totalProfit / 10000)
    + (completedLoansLog.length * 50)
    - (defaultsLog.length * 100)
    + (reputation * 5)
    + (difficulty === 'hard' ? 500 : difficulty === 'medium' ? 200 : 0)
  );

  return (
    <div className="min-h-screen bg-dark-900 bg-grid flex items-center justify-center p-4">
      <div className="max-w-2xl w-full animate-slide-up">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-7xl mb-4">💀</div>
          <h1 className="text-4xl md:text-5xl font-black text-neon-red neon-text-red mb-2">
            BANK COLLAPSED
          </h1>
          <p className="text-gray-400 text-lg">
            Your bank ran out of capital after {month} months.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="glass-panel p-4 text-center">
            <div className="text-xs text-gray-500 mb-1">Months</div>
            <div className="text-2xl font-black text-white font-mono">{month}</div>
          </div>
          <div className="glass-panel p-4 text-center">
            <div className="text-xs text-gray-500 mb-1">Total Profit</div>
            <div className={`text-2xl font-black font-mono ${totalProfit >= 0 ? 'text-neon-green' : 'text-neon-red'}`}>
              {formatMoney(totalProfit)}
            </div>
          </div>
          <div className="glass-panel p-4 text-center">
            <div className="text-xs text-gray-500 mb-1">Default Rate</div>
            <div className="text-2xl font-black font-mono text-neon-yellow">{defaultRate.toFixed(1)}%</div>
          </div>
          <div className="glass-panel p-4 text-center">
            <div className="text-xs text-gray-500 mb-1">Loans Made</div>
            <div className="text-2xl font-black text-white font-mono">{totalLoansApproved}</div>
          </div>
        </div>

        {/* Chart */}
        {chartData.length > 1 && (
          <div className="glass-panel p-4 mb-6">
            <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3">Your Journey</h3>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="goGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff3366" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ff3366" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="month" stroke="#4b5563" tick={{ fontSize: 10 }} />
                <YAxis stroke="#4b5563" tick={{ fontSize: 10 }} tickFormatter={v =>
                  Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(0)}M` : `${(v / 1e3).toFixed(0)}K`
                } />
                <Area type="monotone" dataKey="capital" stroke="#ff3366" fill="url(#goGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Extra stats */}
        <div className="glass-panel p-4 mb-6">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Starting Capital</span>
              <span className="font-mono text-white">{formatMoney(initialCapital)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">ROI</span>
              <span className={`font-mono ${roi >= 0 ? 'text-neon-green' : 'text-neon-red'}`}>{roi.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Loans Completed</span>
              <span className="font-mono text-neon-green">{completedLoansLog.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Loans Defaulted</span>
              <span className="font-mono text-neon-red">{defaultsLog.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Loans Rejected</span>
              <span className="font-mono text-gray-300">{totalLoansRejected}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Difficulty</span>
              <span className="font-mono text-white capitalize">{difficulty}</span>
            </div>
          </div>
        </div>

        {/* Achievements */}
        {unlockedAchievements.size > 0 && (
          <div className="glass-panel p-4 mb-6">
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">🏆 Achievements ({unlockedAchievements.size}/{ACHIEVEMENTS.length})</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {ACHIEVEMENTS.map(a => {
                const unlocked = unlockedAchievements.has(a.id);
                return (
                  <div key={a.id} className={`p-2 rounded-lg text-center ${unlocked ? 'bg-neon-yellow/10 border border-neon-yellow/20' : 'bg-dark-600/30 opacity-40'}`}>
                    <div className="text-lg">{unlocked ? a.emoji : '🔒'}</div>
                    <div className="text-xs text-gray-300 font-semibold mt-0.5">{a.title}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Score */}
        <div className="glass-panel p-4 text-center mb-6">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Final Score</div>
          <div className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-neon-green via-neon-blue to-neon-purple font-mono">
            {Math.round(score).toLocaleString()}
          </div>
        </div>

        {/* Retry */}
        <div className="text-center">
          <button onClick={resetGame} className="btn-primary text-lg px-12 py-4">
            🏦 Try Again
          </button>
        </div>
      </div>
    </div>
  );
}
